import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import Loader from '../Loader';
import Pagination from 'react-js-pagination';

const RegistrationLanguage = ({ token, expire, setToken, setExpire, name, setName, access, selectedCid }) => {
    const [languages,  setLanguages]  = useState([]);
    const [totalItem,  setTotalItem]  = useState(0);
    const [page,       setPage]       = useState(1);
    const [showAll,    setShowAll]    = useState(false);
    const [form,       setForm]       = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [popupOpen,  setPopupOpen]  = useState(true);
    const [msg,        setMsg]        = useState('');
    const [isSuccess,  setIsSuccess]  = useState(false);
    const [expandedTNs, setExpandedTNs] = useState({});
    const [confirmModal, setConfirmModal] = useState(null); // {type, langId, action}

    useEffect(() => { if (token && selectedCid) loadLanguages(); }, [token, selectedCid, page, showAll]);

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        if (expire * 1000 < Date.now()) {
            const res = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${res.data.accessToken}`;
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setExpire(decoded.exp);
        }
        return config;
    }, err => Promise.reject(err));

    const showMsg = (text, success = true) => { setMsg(text); setIsSuccess(success); };

    const loadLanguages = async (pg) => {
        setPopupOpen(true);
        try {
            const currentPage = pg !== undefined ? pg : page;
            const res = await axiosJWT.get(
                `${baseUrl}/registration/setup/language?cid=${selectedCid}&page=${currentPage}&limit=20&show_all=${showAll}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLanguages(res.data.data); setTotalItem(res.data.totalItem);
            if (pg !== undefined) setPage(pg);
        } catch (e) { console.log(e); }
        setPopupOpen(false);
    };

    const loadTNs = async (langId) => {
        if (expandedTNs[langId]) { setExpandedTNs(prev => ({ ...prev, [langId]: null })); return; }
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/setup/language/${langId}/tns`, { headers: { Authorization: `Bearer ${token}` } });
            setExpandedTNs(prev => ({ ...prev, [langId]: res.data.data }));
        } catch (e) { console.log(e); }
    };

    const handleSave = async (action) => {
        const errs = {};
        if (!form.short_description?.trim()) errs.desc = 'Short description required.';
        if (!form.registration_language?.trim()) errs.lang = 'Language text required.';
        if (form.registration_language?.length > 5000) errs.lang = 'Max 5000 characters.';
        setFormErrors(errs);
        if (Object.keys(errs).length > 0) return;

        if (form.id && !action) {
            setConfirmModal({ type: 'edit' });
            return;
        }

        setPopupOpen(true);
        try {
            const res = await axiosJWT.post(`${baseUrl}/registration/setup/language/save`,
                { ...form, bap_bbd_id: selectedCid, action: action || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showMsg(res.data.msg); setForm(null); setConfirmModal(null);
            setPopupOpen(false);
            await loadLanguages(1);
        } catch (e) {
            showMsg(e.response?.data?.msg || 'Error saving.', false);
            setPopupOpen(false);
        }
    };

    const handleReactivate = async (id) => {
        if (!window.confirm('Reactivate this Registration Language?\n\nTN associations that were deactivated with this language will be restored, provided their linked Registration Details is also active.')) return;
        try {
            setPopupOpen(true);
            const res = await axiosJWT.post(`${baseUrl}/registration/setup/language/reactivate/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg); loadLanguages();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error reactivating.', false); }
        setPopupOpen(false);
    };

    const handleDeactivate = async (id) => {
        if (!window.confirm('By selecting that the Registration Language is no longer active, all associated TN will be deactivated.\n\nConfirm YES?')) return;
        try {
            setPopupOpen(true);
            const res = await axiosJWT.post(`${baseUrl}/registration/setup/language/deactivate/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
            showMsg(res.data.msg); loadLanguages();
        } catch (e) { showMsg(e.response?.data?.msg || 'Error deactivating.', false); }
        setPopupOpen(false);
    };

    const charCount = form?.registration_language?.length || 0;

    return (
        <div>
            {popupOpen && <Loader />}
            <p className={`has-text-centered ${isSuccess ? 'success' : 'error'}`}>{msg}</p>

            {/* Confirm Modal for Edit */}
            {confirmModal?.type === 'edit' && (
                <div className="modal-overlay" style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center' }}>
                    <div style={{ background:'#fff',padding:'30px',borderRadius:'8px',maxWidth:'500px',width:'90%' }}>
                        <h4>Confirm Update</h4>
                        <p>Please confirm that all TN listed are to be updated to have the new Registration Language.</p>
                        <div style={{ display:'flex',gap:'10px',marginTop:'15px',flexWrap:'wrap' }}>
                            <button className="btn btn-primary" onClick={() => handleSave('confirm')}>CONFIRM</button>
                            <button className="btn btn-warning" onClick={() => handleSave('new')}>MAKE NEW ENTRY WITH NO TN ASSOCIATED</button>
                            <button className="btn btn-secondary" onClick={() => { setConfirmModal(null); handleSave('discard'); }}>DISCARD CHANGES</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="col-12 common-white-shadow-background setting-section">
                <div className="row">
                    <div className="col-6">
                        <label style={{ marginRight: '10px' }}>
                            <input type="checkbox" style={{ width:'auto' }} checked={showAll} onChange={e => { setShowAll(e.target.checked); setPage(1); }} /> Include Inactive Records
                        </label>
                    </div>
                    <div className="col-6 inventory-btn">
                        {!form && (
                            <div className="addnew-button">
                                <a href="#" onClick={e => { e.preventDefault(); setForm({ short_description:'', registration_language:'', available_for_use_start_date:'' }); setFormErrors({}); }}>Add</a>
                            </div>
                        )}
                    </div>
                </div>

                {form && (
                    <div style={{ padding:'15px',background:'#f9f9f9',borderRadius:'6px',marginBottom:'15px' }}>
                        <h4>{form.id ? 'Edit Language' : 'Add Language'}</h4>
                        <div className="row">
                            <div className="col-md-4">
                                <label>Short Description * (100 chars)</label>
                                <input type="text" className="form-control" maxLength={100} value={form.short_description} onChange={e => setForm({...form, short_description: e.target.value})} />
                                {formErrors.desc && <div className="error error-message">{formErrors.desc}</div>}
                            </div>
                            <div className="col-md-3">
                                <label>Available From Date</label>
                                <input type="date" className="form-control" value={form.available_for_use_start_date || ''} onChange={e => setForm({...form, available_for_use_start_date: e.target.value})} />
                            </div>
                        </div>
                        <div className="row" style={{ marginTop:'10px' }}>
                            <div className="col-12">
                                <label>Registration Language * <span style={{ color: charCount > 5000 ? 'red' : '#666' }}>({charCount}/5000 characters)</span></label>
                                <textarea
                                    className="form-control"
                                    rows={8}
                                    value={form.registration_language}
                                    onChange={e => setForm({...form, registration_language: e.target.value})}
                                    style={{ resize:'vertical' }}
                                />
                                {formErrors.lang && <div className="error error-message">{formErrors.lang}</div>}
                            </div>
                        </div>
                        <div style={{ marginTop:'10px', display:'flex', gap:'10px' }}>
                            <button className="btn btn-primary" onClick={() => handleSave(null)}>Save</button>
                            <button className="btn btn-secondary" onClick={() => setForm(null)}>Cancel</button>
                        </div>
                    </div>
                )}

                <div className="fullwidth-table track-table-body">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width:'auto' }}>ID</th><th style={{ width:'auto' }}>Short Description</th><th style={{ width:'auto' }}>Start Date</th><th style={{ width:'auto' }}>Status</th><th style={{ width:'auto' }}>TN Count</th><th style={{ width:'auto' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {languages.map((row, i) => (
                                <React.Fragment key={i}>
                                    <tr>
                                        <td style={{ textAlign:'left' }}>{row.reg_lang_id}</td>
                                        <td style={{ textAlign:'left' }}>{row.short_description}</td>
                                        <td style={{ textAlign:'left' }}>{row.available_for_use_start_date ? row.available_for_use_start_date.split('T')[0] : '—'}</td>
                                        <td style={{ textAlign:'left', paddingLeft:0 }}><div style={{ maxWidth:'80px' }}>{row.available_for_use_end_date ? <a href="#" className="false">False</a> : <a href="#" className="true">True</a>}</div></td>
                                        <td style={{ textAlign:'left' }}>
                                            <a href="#" onClick={e => { e.preventDefault(); loadTNs(row.reg_lang_id); }}>
                                                {[row.all_tn_count > 0 ? 'All TNs' : null, row.tn_count > 0 ? `${row.tn_count} TN(s)` : null, row.tag_count > 0 ? `${row.tag_count} Tag(s)` : null].filter(Boolean).join(', ') || '0'} {expandedTNs[row.reg_lang_id] ? '▲' : '▼'}
                                            </a>
                                        </td>
                                        <td style={{ textAlign:'left' }}>
                                            <a href="#" onClick={e => { e.preventDefault(); setForm({ ...row, id: row.reg_lang_id, available_for_use_start_date: row.available_for_use_start_date ? row.available_for_use_start_date.split('T')[0] : '' }); setFormErrors({}); }}>
                                                <img src={`${process.env.PUBLIC_URL}/images/bx-edit.png`} alt="Edit" />
                                            </a>
                                            {!row.available_for_use_end_date ? (
                                                <a href="#" style={{ marginLeft:'8px' }} onClick={e => { e.preventDefault(); handleDeactivate(row.reg_lang_id); }}>
                                                    <img src={`${process.env.PUBLIC_URL}/images/bx-x-circle.png`} alt="Deactivate" title="Deactivate" />
                                                </a>
                                            ) : (
                                                <a href="#" style={{ marginLeft:'8px' }} onClick={e => { e.preventDefault(); handleReactivate(row.reg_lang_id); }}>
                                                    <img src={`${process.env.PUBLIC_URL}/images/success.png`} alt="Reactivate" title="Reactivate" />
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                    {expandedTNs[row.reg_lang_id] && (
                                        <tr>
                                            <td colSpan={6} style={{ background:'#f5f5f5',padding:'10px' }}>
                                                {expandedTNs[row.reg_lang_id].length === 0
                                                    ? <em>No TN associations.</em>
                                                    : <table style={{ width:'100%' }}>
                                                        <thead><tr><th>TN</th><th>Tag</th><th>Details</th></tr></thead>
                                                        <tbody>
                                                            {expandedTNs[row.reg_lang_id].map((t, j) => (
                                                                <tr key={j}><td>{t.tn || '—'}</td><td>{t.tag || '—'}</td><td>{t.registration_short_description}</td></tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                }
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    {totalItem > 20 && <Pagination activePage={page} itemsCountPerPage={20} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={setPage} />}
                </div>
            </div>
        </div>
    );
};

export default RegistrationLanguage;
